import { ExtensionBase } from "./extensionBase";
import { TextControl } from "../controls/textControl";
import { UiUtils } from "../common/uiUtils";
import * as SmartDrawings from "../smartDrawings";

/**
 * An extension for navigating a PDF document.
 */
export class PdfNavExtension extends ExtensionBase {

    static extensionName = "PdfNavExtension";
    private readonly documentBrowserExtension = "Autodesk.DocumentBrowser";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private toolbarDiv: HTMLDivElement;
    private positionText: TextControl;

    private rewindButton:       Autodesk.Viewing.UI.Button;
    private backButton:         Autodesk.Viewing.UI.Button;
    private forwardButton:      Autodesk.Viewing.UI.Button;
    private fastForwardButton:  Autodesk.Viewing.UI.Button;
    private buttonGroup:        Autodesk.Viewing.UI.ControlGroup;
   
    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
    }

    static registerExtension() {
        super.registerExtension(PdfNavExtension.extensionName, PdfNavExtension);
    }

    load(): boolean {
        console.log(`${PdfNavExtension.extensionName} is loaded!`);
        return super.load();
    };

    unload(): boolean {
        const toolbar = this.getControlGroup("modelTools");

        toolbar.removeControl(this.rewindButton);
        toolbar.removeControl(this.backButton);
        toolbar.removeControl(this.positionText);
        toolbar.removeControl(this.forwardButton);
        toolbar.removeControl(this.fastForwardButton);

        return super.unload();
    };

    onAllModelsLoaded() {
        this.viewer.removeEventListener(
            SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, () => this.onAllModelsLoaded());

        const toolbar = this.viewer.getToolbar(false);
        const doc = toolbar.getControl("toolbar-documentModels") as any;

        if (doc == null) {
            console.log("doc is null");
            return;
        }

        console.log("doc is not null");
    }

    onToolbarCreated(): void {
        if (!this.viewer.model.isPdf())
            return;

        this.createToolbarButtons();

        // Create & adjust text to toolbar
        this.positionText = new TextControl("SmartDrawings.PdfPosition") as any;
        this.positionText.addClass("adsk-button");

        const positionText = this.positionText as any;
        const span = positionText.span as any;

        span.classList.remove("textcontrol-span");
        span.setAttribute("style", "white-space: nowrap; margin-top: 4px;");

        this.buttonGroup = this.getControlGroup("modelTools");
       
        this.buttonGroup.addControl(this.rewindButton);
        this.buttonGroup.addControl(this.backButton);
        this.buttonGroup.addControl(this.positionText);
        this.buttonGroup.addControl(this.forwardButton);
        this.buttonGroup.addControl(this.fastForwardButton);

        this.updatePosition();
    }

    private createToolbarButtons() {
        this.rewindButton = UiUtils.createButton({
            id: "SmartDrawings.PdfBeginButton",
            tooltip: "First page",
            iconClass: "ion-md-rewind",
            onClick: () => {
               this.pdfGotoBeginning();
            }
        });

        this.backButton = UiUtils.createButton({
            id: "SmartDrawings.PdfPrevButton",
            tooltip: "Previous page",
            iconClass: "ion-md-arrow-dropleft",
            onClick: () => {
                this.pdfGotoPrev();
            }
        });

        this.forwardButton = UiUtils.createButton({
            id: "SmartDrawings.PdfNextButton",
            tooltip: "Next page",
            iconClass: "ion-md-arrow-dropright",
            onClick: () => {
                this.pdfGotoNext();
            }
        });

        this.fastForwardButton = UiUtils.createButton({
            id: "SmartDrawings.PdfEndButton",
            tooltip: "Last page",
            iconClass: "ion-md-fastforward",
            onClick: () => {
                this.pdfGotoEnd();
            }
        });
    }

    private getCurrentPageIndex() : number {
         const currentNode = this.viewer.model.getDocumentNode();
         const rootNode = currentNode.getRootNode();
         const len = rootNode.children.length;

         for (let n = 0; n < len; n++) {
             if (currentNode === rootNode.children[n])
                return n;
         }

         return -1;
    }

    private getPdfPositionString() : string {
        const index = this.getCurrentPageIndex();
        const len = this.viewer.model.getDocumentNode().getRootNode().children.length;

        return `${index + 1} / ${len}`;
    }

    private updatePosition() {
        this.positionText.setText(this.getPdfPositionString());
    }

    private pdfGotoBeginning() {
        if (this.getCurrentPageIndex() === 0) return;

        const rootNode = this.viewer.model.getDocumentNode().getRootNode();
        this.pdfChangeTo(rootNode.children[0].findParentGeom2Dor3D());
    }

    private pdfGotoEnd() {
        const rootNode = this.viewer.model.getDocumentNode().getRootNode();
        const endIndex = rootNode.children.length - 1;

        if (this.getCurrentPageIndex() === endIndex)
            return;

        const lastDoc = rootNode.children[endIndex];
        const bubbleNode = lastDoc.findParentGeom2Dor3D();

        this.pdfChangeTo(bubbleNode);
    }

    private pdfGotoPrev() {
        const currentIndex = this.getCurrentPageIndex();

        if (currentIndex === 0) return;

        const rootNode = this.viewer.model.getDocumentNode().getRootNode();
        const nextDoc = rootNode.children[currentIndex - 1];
        const bubbleNode = nextDoc.findParentGeom2Dor3D();

        this.pdfChangeTo(bubbleNode);
    }

    private pdfGotoNext() {
        const currentIndex = this.getCurrentPageIndex();
        const rootNode = this.viewer.model.getDocumentNode().getRootNode();

        if (currentIndex === rootNode.children.length - 1)
            return;

        const nextDoc = rootNode.children[currentIndex + 1];

        const bubbleNode = nextDoc.findParentGeom2Dor3D();
        this.pdfChangeTo(bubbleNode);
    }

    private pdfChangeTo(bubbleNode : Autodesk.Viewing.BubbleNode) {
        // Make sure DocumentBrowser is reloaded after changing page
        const extensions = this.viewer.config.extensions;
        if (extensions.indexOf(this.documentBrowserExtension) === -1)
            extensions.push(this.documentBrowserExtension);

        const currentDoc =
            this.viewer.model.getDocumentNode().getRootNode().getDocument();
        
        this.viewer.loadDocumentNode(currentDoc, bubbleNode);
    }
}